import type { FC, ReactNode } from 'react'
import {
    Stack,
    Group,
    Badge,
    Divider,
    Text,
    UnstyledButton,
} from '@mantine/core'
import {
    IconAdjustmentsHorizontal,
    IconChartDots3,
    IconSend,
} from '@tabler/icons-react'
import type { AudienceConfiguration } from '@/lib/domain'
import { segmentKey } from '@/features/segment/api'
import { useSegmentStubs } from '@/features/segment/queries'
import type { SegmentStage } from '@/features/segment/components/SegmentDetail'
import { countFilters } from './renderMappers'
import { DeleteButton } from '../../../components/DeleteButton'
import classes from './SegmentStageFooter.module.css'

const StageButton: FC<{
    icon: ReactNode
    label: string
    value: string
    color: string
    onClick: () => void
}> = ({ icon, label, value, color, onClick }) => (
    <UnstyledButton
        onClick={onClick}
        p={6}
        style={{ borderRadius: 'var(--mantine-radius-sm)' }}
    >
        <Stack gap={4} align='center'>
            <Group gap={4} c='dimmed' wrap='nowrap'>
                {icon}
                <Text size='xs' fw={600} className={classes.stageText}>
                    {label}
                </Text>
            </Group>
            <Badge
                size='xs'
                variant='light'
                color={color}
                className={classes.stageText}
            >
                {value}
            </Badge>
        </Stack>
    </UnstyledButton>
)

export interface SegmentStageFooterProps {
    config: AudienceConfiguration
    threadId: string
    onOpenDetail: (config: AudienceConfiguration, stage: SegmentStage) => void
    onDelete: () => void | Promise<unknown>
    isDeleting?: boolean
}

// Shared lifecycle footer (makeup -> model -> distribute) rendered INSIDE each
// cart card, below a full-width divider. Reads the segment's lifecycle stub and
// opens the shared SegmentDetail modal at the chosen stage. The remove-from-cart
// action lives here too, pinned to the right of the stage row.
export const SegmentStageFooter: FC<SegmentStageFooterProps> = ({
    config,
    threadId,
    onOpenDetail,
    onDelete,
    isDeleting = false,
}) => {
    const { lifecycleFor } = useSegmentStubs()
    const life = lifecycleFor(segmentKey(config), threadId)

    const modelValue =
        life.modelStatus === 'ready'
            ? 'Ready'
            : life.modelStatus === 'draft'
              ? 'Draft'
              : 'Not modeled'
    const distValue =
        life.distributeStatus === 'live'
            ? `Live · ${life.liveDestinationCount}`
            : life.distributeStatus === 'attention'
              ? 'Needs setup'
              : life.distributeStatus === 'pending'
                ? 'Scheduled'
                : 'Not sent'
    const distColor =
        life.distributeStatus === 'live'
            ? 'teal'
            : life.distributeStatus === 'attention'
              ? 'orange'
              : 'gray'
    // "Makeup" surfaces how the segment is composed — the number of field/value
    // filter conditions that define it.
    const filterCount = countFilters(config)

    return (
        <>
            <Divider />
            <Group
                gap={4}
                px='xs'
                py={6}
                wrap='nowrap'
                align='center'
                className={classes.footer}
            >
                <Group grow gap={4} style={{ flex: 1, minWidth: 0 }}>
                    <StageButton
                        icon={<IconAdjustmentsHorizontal size={14} />}
                        label='Makeup'
                        value={`${filterCount} ${filterCount === 1 ? 'filter' : 'filters'}`}
                        color={filterCount > 0 ? 'blue' : 'gray'}
                        onClick={() => onOpenDetail(config, 'makeup')}
                    />
                    <StageButton
                        icon={<IconChartDots3 size={14} />}
                        label='Model'
                        value={modelValue}
                        color={life.modelStatus === 'ready' ? 'teal' : 'gray'}
                        onClick={() => onOpenDetail(config, 'model')}
                    />
                    <StageButton
                        icon={<IconSend size={14} />}
                        label='Distribute'
                        value={distValue}
                        color={distColor}
                        onClick={() => onOpenDetail(config, 'distribute')}
                    />
                </Group>
                <DeleteButton
                    onDelete={onDelete}
                    isLoading={isDeleting}
                    title='Remove from cart'
                    confirmTitle='Remove Configuration'
                    confirmMessage='Are you sure you want to remove this segment from your cart?'
                    size='md'
                />
            </Group>
        </>
    )
}
